import React, { useState, useEffect } from 'react'
import { NavLink as RouterLink, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Button, Collapse, ListItem, styled } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

const StyledListItem = styled(ListItem)(({ theme }) => ({
  display: 'block',
  paddingTop: 0,
  paddingBottom: 0
}))

const StyledButton = styled(Button)(({ isActive }) => ({
  color: isActive ? 'primary' : '#ffffff',
  padding: '12px 14px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  alignContent: 'center',
  display: 'grid',
  gridTemplateColumns: '24px minmax(0, 1fr) auto',
  columnGap: '12px',
  textTransform: 'none',
  marginBottom: '8px',
  letterSpacing: 0,
  width: '100%',
  minWidth: 0,
  fontWeight: '500',
  fontSize: '15px',
  borderRadius: '10px',
  backgroundColor: isActive ? '#ffffff' : 'transparent',
  transition: 'all 0.2s ease',
  textAlign: 'left',
  '& .icon-wrap': {
    width: '24px',
    minWidth: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '& .icon': {
    color: isActive ? 'primary' : '#ffffff',
    fontSize: '20px'
  },
  '& .label': {
    display: 'block',
    minWidth: 0,
    width: '100%',
    textAlign: 'left',
    lineHeight: 1.3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    justifySelf: 'stretch'
  },
  '& .expand-icon': {
    justifySelf: 'end',
    fontSize: '20px'
  },
  '&:hover': {
    color: '#E31B54 !important',
    backgroundColor: '#ffffff',
    '& .icon': {
      color: '#E31B54 !important'
    }
  }
}))

const StyledButtonLeaf = styled(Button)(({ depth, isActive }) => ({
  color: isActive ? 'primary' : '#FFFFFF',
  justifyContent: 'flex-start',
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '24px minmax(0, 1fr)',
  columnGap: '12px',
  textTransform: 'none',
  letterSpacing: 0,
  width: '100%',
  minWidth: 0,
  marginBottom: '5px',
  opacity: 1,
  fontWeight: '500',
  fontSize: '15px',
  borderRadius: '10px',
  minHeight: '48px',
  padding: '10px 14px',
  paddingLeft: 16 + 10 * depth,
  backgroundColor: isActive ? '#FFFFFF' : 'transparent',
  transition: 'all 0.2s ease',
  textAlign: 'left',
  alignContent: 'center',
  whiteSpace: 'normal',
  '& .icon': {
    color: isActive ? 'primary' : '#FFFFFF',
    fontSize: '20px'
  },
  '& .icon-wrap': {
    width: '24px',
    minWidth: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '& .label': {
    display: 'block',
    minWidth: 0,
    width: '100%',
    textAlign: 'left',
    lineHeight: 1.3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    justifySelf: 'stretch'
  },
  '&:hover': {
    color: '#E31B54 !important',
    backgroundColor: '#FFFFFF',
    '& .icon': {
      color: '#E31B54 !important'
    }
  }
}))

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  tabview,
  setSelectedTab,
  ...rest
}) => {
  const [open, setOpen] = useState(openProp)
  const location = useLocation()

  useEffect(() => {
    if (tabview === 'Sniper') {
      setSelectedTab(tabview)
    }
  }, [tabview, setSelectedTab])

  useEffect(() => {
    setOpen(openProp)
  }, [openProp])

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const isActive = Boolean(href && location.pathname.includes(href))

  if (children) {
    return (
      <StyledListItem className={className} disableGutters key={title} {...rest}>
        <StyledButton onClick={handleToggle} isActive={openProp}>
          {Icon && (
            <span className="icon-wrap">
              <Icon className="icon" />
            </span>
          )}
          <span className="label">{title}</span>
          {open ? (
            <ExpandLessIcon className="expand-icon" />
          ) : (
            <ExpandMoreIcon className="expand-icon" />
          )}
        </StyledButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </StyledListItem>
    )
  }

  return (
    <StyledListItem className={className} disableGutters key={title} {...rest}>
      <StyledButtonLeaf component={RouterLink} to={href} depth={depth} isActive={isActive}>
        {Icon && (
          <span className="icon-wrap">
            <Icon className="icon" />
          </span>
        )}
        <span className="label">{title}</span>
        {Info && <Info />}
      </StyledButtonLeaf>
    </StyledListItem>
  )
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  tabview: PropTypes.string,
  setSelectedTab: PropTypes.func
}

NavItem.defaultProps = {
  open: false
}

export default NavItem
